import { useSyncExternalStore } from 'react';

/**
 * A folha de Pessoas (Conversas e Contatos) no palco: em que estágio está, onde
 * cada lista estava rolada e o que a pessoa buscava. Fica fora do React para
 * sobreviver à ida a uma conversa e à volta: a lista volta exatamente onde estava.
 */
export type PeopleDetent = 'half' | 'full';
export type PeoplePane = 'conversas' | 'contatos';

type PaneMemory = { scroll: number; query: string; filter: string };

type PeopleSheetState = {
  detent: PeopleDetent;
  /** O atalho do trilho pediu a folha: ela sobe do rodapé até o meio. */
  fromRail: boolean;
  panes: Record<PeoplePane, PaneMemory>;
};

const EMPTY_PANE: PaneMemory = { scroll: 0, query: '', filter: 'all' };

let state: PeopleSheetState = {
  detent: 'full',
  fromRail: false,
  panes: { conversas: { ...EMPTY_PANE }, contatos: { ...EMPTY_PANE } },
};
const listeners = new Set<() => void>();

function set(next: Partial<PeopleSheetState>): void {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener());
}

/** Pessoas pelo Hoje fixo: a folha de Pessoas toma o lugar do Hoje no meio, sem subir de novo. */
export function openPeopleFromDock(): void {
  set({ detent: 'half', fromRail: false });
}

/** O atalho Pessoas: a folha abre no meio, sobre o rosto. */
export function openPeopleFromRail(): void {
  set({ detent: 'half', fromRail: true });
}

export function setPeopleDetent(detent: PeopleDetent): void {
  if (state.detent !== detent) set({ detent, fromRail: false });
}

/** A folha fechou: a próxima abertura por link direto vem inteira. */
export function resetPeopleSheet(): void {
  set({ detent: 'full', fromRail: false });
}

export function rememberPane(pane: PeoplePane, memory: Partial<PaneMemory>): void {
  const current = state.panes[pane];
  const next = { ...current, ...memory };
  if (
    next.scroll === current.scroll &&
    next.query === current.query &&
    next.filter === current.filter
  ) {
    return;
  }
  state = { ...state, panes: { ...state.panes, [pane]: next } };
  // Rolagem e busca não redesenham ninguém: quem precisa lê na hora.
}

export function paneMemory(pane: PeoplePane): PaneMemory {
  return state.panes[pane];
}

export function peopleSheet(): PeopleSheetState {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function usePeopleSheet(): PeopleSheetState {
  return useSyncExternalStore(subscribe, peopleSheet, peopleSheet);
}

/** Só para os testes. */
export function resetPeopleSheetAll(): void {
  state = {
    detent: 'full',
    fromRail: false,
    panes: { conversas: { ...EMPTY_PANE }, contatos: { ...EMPTY_PANE } },
  };
}
